import dynamic from 'next/dynamic';
import { BASE_PATHS, useRouter } from '@belong/common';
import { Text, Button, Link, Image } from '@belong/ui';
import '@reach/menu-button/styles.css';
import to from 'await-to-js';
import {
  ResidentAccountBookmark,
  ResidentAccountSummary,
  ResidentFavoriteModel,
  TourRequestModel,
  OwnerPropertiesSummary,
} from 'src/api/models';
import { HoverMenuButton } from 'src/components/hover-menu-button/hover-menu-button';
import { LoginModalProps } from 'src/features/auth/login-modal/login-modal';
import { useLogout } from 'src/features/auth/queries';
import { useCustomerioAnalytics } from 'src/features/document/customerio';
import { useHomeownerProperties, useManualFetchHomeownerProperties } from 'src/features/homeowners/queries';
import { useGetResidentTourRequests } from 'src/features/house-page/queries';
import { useResidentBookmarks, useResidentLeases, useResidentFavorites } from 'src/features/residents';
import { isTourRequestActive } from 'src/features/residents/utils';
import { UserClaimModel, useUser, useUserClaims, useUserProfile } from 'src/features/users/useUser';
import { useModal } from 'src/hooks/useModal';
import { HowItWorksProps } from 'src/modals/how-it-works/how-it-works';
import type { HeaderVariant } from '../header';

const HowItWorks = dynamic<HowItWorksProps>(() =>
  import('src/modals/how-it-works/how-it-works').then((mod) => mod.HowItWorks)
);

const LoginModal = dynamic<LoginModalProps>(() =>
  import('src/features/auth/login-modal/login-modal').then((mod) => mod.LoginModal)
);

type DefaultLink = {
  to: string;
  label: React.ReactNode;
};

export const DEFAULT_LINKS: Record<string, DefaultLink> = {
  YOUR_HOMES: {
    to: BASE_PATHS.HOMEOWNER_SETUP_FLOW,
    label: 'MY HOMES',
  },
  YOUR_HOME: {
    to: BASE_PATHS.ACCOUNTS,
    label: 'MY HOME',
  },
  FAVORITES_EXIST: {
    to: `${BASE_PATHS.ACCOUNTS}/favorites`,
    label: (
      <Text as="span" variant="p1" className="flex items-center" fontWeight="semibold">
        FAVORITES
        <div className="ml-xs" style={{ width: 13 }}>
          <Image src="/icons/heart_icon.svg" alt="" />
        </div>
      </Text>
    ),
  },
  FAVORITES_NOT_EXISTING_YET: {
    to: `${BASE_PATHS.ACCOUNTS}/favorites`,
    label: 'FAVORITES',
  },

  TOURS_AND_APPS: {
    to: `${BASE_PATHS.ACCOUNTS}/tours-and-apps`,
    label: 'TOURS & APPS',
  },
  YOUR_PROFILE: {
    to: `${BASE_PATHS.ACCOUNTS}/profile`,
    label: 'MY PROFILE',
  },
  SIGN_OUT: {
    to: '',
    label: 'SIGN OUT',
  },
};

type GetMenuLinksParams = {
  userId?: string;
  userClaims?: UserClaimModel;
  leases?: ResidentAccountSummary;
  favorites?: ResidentFavoriteModel[];
  bookmarks?: ResidentAccountBookmark;
  tourRequests?: TourRequestModel[];
  homeownerProperties?: OwnerPropertiesSummary;
};

function getMenuLinks({
  userId,
  userClaims,
  leases,
  favorites,
  bookmarks,
  tourRequests,
  homeownerProperties,
}: GetMenuLinksParams) {
  const listOfLinks: DefaultLink[] = [];

  if (!userId) {
    return listOfLinks;
  }

  if (
    userClaims?.isHomeOwner &&
    Array.isArray(homeownerProperties?.properties) &&
    homeownerProperties?.properties.length > 0
  ) {
    listOfLinks.push(DEFAULT_LINKS.YOUR_HOMES);
  } else if (userClaims?.isResident) {
    if (leases?.leases?.length) {
      listOfLinks.push(DEFAULT_LINKS.YOUR_HOME);
    }
  }

  if (favorites?.length) {
    listOfLinks.push(DEFAULT_LINKS.FAVORITES_EXIST);
  } else {
    listOfLinks.push(DEFAULT_LINKS.FAVORITES_NOT_EXISTING_YET);
  }

  if (bookmarks?.bookmarks?.length || tourRequests?.length) {
    listOfLinks.push(DEFAULT_LINKS.TOURS_AND_APPS);
  }

  listOfLinks.push(DEFAULT_LINKS.YOUR_PROFILE);

  return listOfLinks;
}

export function DesktopNavigation({ variant = 'primary', theme = 'light' }: DesktopNavigationProps): JSX.Element {
  const router = useRouter();
  const { mutateAsync: logout } = useLogout();
  const customerioAnalytics = useCustomerioAnalytics();

  const { user } = useUser();
  const { userId } = user || {};

  const { data: userProfile } = useUserProfile({ enabled: !!userId });
  const { profileImageUrl } = userProfile || {};

  const { userClaims, refetchUserClaims } = useUserClaims({ enabled: !!userId });
  const { isResident, isHomeOwner } = userClaims || {};

  const { homeownerProperties } = useHomeownerProperties({ enabled: isHomeOwner });
  const { fetchProperties } = useManualFetchHomeownerProperties();

  const { data: leases } = useResidentLeases({ enabled: !!userId && !!isResident });
  const { favorites } = useResidentFavorites();
  const { data: bookmarks } = useResidentBookmarks({ enabled: !!userId && !!isResident });
  const { data: tourRequests } = useGetResidentTourRequests({ enabled: !!userId });

  const [isLoginModalOpen, openLoginModal, closeLoginModal] = useModal();
  const [isHowItWorksModalOpen, openHowItWorksModal, closeHowItWorksModal] = useModal();

  const isDarkTheme = theme === 'dark';

  const menuLinks = getMenuLinks({
    userId,
    userClaims,
    leases,
    favorites,
    bookmarks,
    tourRequests: tourRequests?.filter(isTourRequestActive),
    homeownerProperties,
  });

  async function handleSuccessfulLogin() {
    try {
      const updatedUserClaims = await refetchUserClaims();

      const { isHomeOwner } = updatedUserClaims.data || {};

      if (isHomeOwner) {
        const updateproperties = await fetchProperties();

        router.push(
          Array.isArray(updateproperties?.properties) && updateproperties?.properties.length > 0
            ? BASE_PATHS.HOMEOWNER_SETUP_FLOW
            : '/accounts/profile'
        );
      } else {
        router.push(BASE_PATHS.ACCOUNTS);
      }
    } catch {
      closeLoginModal();
    }
  }

  async function handleLogoutClick() {
    await to(logout());
    customerioAnalytics?.reset();

    router.push('/');
  }

  function isActivePath(path: string): boolean {
    return router.pathname === path;
  }

  return (
    <div className="flex items-center">
      <div className="flex mr-sm">
        <Link href="/homeowners" buttonType={isDarkTheme ? 'light' : 'primary'} className="relative">
          <Text variant="p1" fontWeight="semibold">
            FOR HOMEOWNERS
          </Text>
          {isActivePath('/homeowners') && (
            <div className="absolute -bottom-2xs border-t-2 border-b-0 border-x-0 border-solid border-white w-full" />
          )}
        </Link>
      </div>
      <div className="flex mr-sm">
        <Link href="/residents" buttonType={isDarkTheme ? 'light' : 'primary'} className="relative">
          <Text variant="p1" fontWeight="semibold">
            FOR RESIDENTS
          </Text>
          {isActivePath('/residents') && (
            <div className="absolute -bottom-2xs border-t-2 border-b-0 border-x-0 border-solid border-white w-full" />
          )}
        </Link>
      </div>
      <div className="flex mr-sm">
        <Button variant="text" onClick={openHowItWorksModal} buttonType={variantToButtonTypeMap[variant]}>
          <Text variant="p1" fontWeight="semibold">
            HOW IT WORKS
          </Text>
        </Button>
      </div>
      <div className="flex mr-sm">
        <Link href="/support" buttonType={isDarkTheme ? 'light' : 'primary'} className="relative">
          <Text variant="p1" fontWeight="semibold">
            GET HELP 24/7
          </Text>
          {isActivePath('/support') && (
            <div className="absolute -bottom-2xs border-t-2 border-b-0 border-x-0 border-solid border-navy w-full" />
          )}
        </Link>
      </div>
      {user ? (
        <div className="flex">
          <HoverMenuButton
            href={DEFAULT_LINKS.YOUR_PROFILE.to}
            theme={theme}
            title={
              <div
                className="flex items-center gap-2sm relative"
                style={{
                  top: -5,
                }}
              >
                <Text variant="p1" fontWeight="semibold">
                  {user.firstName}
                </Text>
                {profileImageUrl ? (
                  <div className="w-lg h-lg rounded-full border border-solid overflow-hidden">
                    <Image src={profileImageUrl} alt="" className="object-cover" />
                  </div>
                ) : (
                  <div className="w-lg">
                    <Image src="/icons/profile_img.svg" alt="Profile Image" />
                  </div>
                )}
              </div>
            }
          >
            <>
              {menuLinks.map((item) => (
                <div
                  key={item.to}
                  className="hover:border-l-4 border-transparent border-l-4 box-border px-xs hover:border-navy border-solid border-0 mb-2sm last:mb-0"
                >
                  <Link href={item.to}>
                    {typeof item.label === 'string' ? (
                      <Text fontWeight="semibold" variant="p1" className="uppercase">
                        {item.label}
                      </Text>
                    ) : (
                      <>{item.label}</>
                    )}
                  </Link>
                </div>
              ))}
              <div className="hover:border-l-4 border-transparent border-l-4 box-border px-xs hover:border-navy border-solid border-0 mb-2sm last:mb-0">
                <Button variant="text" onClick={() => handleLogoutClick()}>
                  <Text fontWeight="semibold" variant="p1" className="uppercase">
                    sign out
                  </Text>
                </Button>
              </div>
            </>
          </HoverMenuButton>
        </div>
      ) : (
        <Button variant="text" onClick={openLoginModal} buttonType={variantToButtonTypeMap[variant]}>
          <Text variant="p1" className="hover:text-green" fontWeight="semibold">
            MY ACCOUNT
          </Text>
        </Button>
      )}
      <LoginModal isOpen={isLoginModalOpen} onDismiss={closeLoginModal} onSuccessfulLogin={handleSuccessfulLogin} />
      {isHowItWorksModalOpen && <HowItWorks isOpen={isHowItWorksModalOpen} onDismiss={closeHowItWorksModal} />}
    </div>
  );
}

const variantToButtonTypeMap: Record<HeaderVariant, 'primary' | 'light'> = {
  primary: 'primary',
  secondary: 'light',
  tertiary: 'primary',
};

export type DesktopNavigationProps = {
  variant?: HeaderVariant;
  theme?: 'light' | 'dark';
};
